import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, MessageCircle, Send, MapPin, Loader2 } from "lucide-react";
import api, { apiError, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_MAP, STATUS_MAP, timeAgo } from "@/lib/constants";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function IssueCard({ issue, onUpdate, onRequireAuth }) {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();
  const [supporting, setSupporting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const cat = CATEGORY_MAP[issue.category];
  const status = STATUS_MAP[issue.status] || STATUS_MAP.reported;
  const CatIcon = cat?.icon;

  const support = async () => {
    if (!user) return onRequireAuth?.();
    if (issue.is_mine) return toast.error("You cannot back your own issue");
    if (issue.supported_by_me) return;
    setSupporting(true);
    try {
      const { data } = await api.post(`/issues/${issue.id}/support`);
      patchUser({ coins: data.coins });
      onUpdate?.(data.issue);
      toast.success("Backed with 1 Dimagi coin");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not back issue");
    } finally {
      setSupporting(false);
    }
  };

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) {
      try {
        const { data } = await api.get(`/issues/${issue.id}/comments`);
        setComments(data);
      } catch {
        setComments([]);
      }
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!user) return onRequireAuth?.();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/issues/${issue.id}/comments`, { text });
      setComments((c) => [...(c || []), data]);
      setText("");
      onUpdate?.({ ...issue, comment_count: (issue.comment_count || 0) + 1 });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <article data-testid="issue-post-card" className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <button className="flex items-center gap-2.5" onClick={() => navigate(`/profile/${issue.author_id}`)}>
            <Avatar seed={issue.author_avatar_seed} alias={issue.author_alias} size={40} />
            <div className="text-left leading-tight">
              <div className="text-sm font-semibold hover:text-saffron transition-colors">{issue.author_alias}</div>
              <div className="text-[11px] text-muted-foreground font-mono">{timeAgo(issue.created_at)}</div>
            </div>
          </button>
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white"
            style={{ background: cat?.color }}
          >
            {CatIcon && <CatIcon size={12} />} {cat?.label}
          </span>
        </div>

        <h3 className="mt-3 font-display font-bold text-lg leading-snug">{issue.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{issue.description}</p>

        <div className="mt-2 flex items-center gap-3 text-xs">
          {issue.location && (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <MapPin size={12} /> {issue.location}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: status.color }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: status.color }} /> {status.label}
          </span>
        </div>
      </div>

      {issue.image_path && (
        <img
          data-testid="issue-post-image"
          src={imageUrl(issue.image_path)}
          alt={issue.title}
          className="w-full max-h-[420px] object-cover border-y border-border"
          loading="lazy"
        />
      )}

      <div className="flex items-center gap-2 p-3 border-t border-border">
        <Button
          data-testid="issue-support-coin-button"
          size="sm"
          variant={issue.supported_by_me ? "default" : "outline"}
          disabled={supporting || issue.supported_by_me || issue.is_mine}
          onClick={support}
          className={issue.supported_by_me ? "bg-coin text-black hover:bg-coin/90 gap-1.5" : "gap-1.5"}
        >
          {supporting ? <Loader2 size={15} className="animate-spin" /> : <Coins size={15} />}
          {issue.supported_by_me ? "Backed" : "Back (1 coin)"}
          <span className="font-mono">· {issue.support_count}</span>
        </Button>

        <Button data-testid="issue-comment-button" size="sm" variant="ghost" onClick={toggleComments} className="gap-1.5">
          <MessageCircle size={15} /> {issue.comment_count || 0}
        </Button>

        <span className="ml-auto text-xs text-coin font-mono inline-flex items-center gap-1">
          <Coins size={13} /> {issue.coins_received} backed
        </span>
      </div>

      {showComments && (
        <div className="px-4 pb-4 space-y-3 border-t border-border bg-background/40">
          <div className="pt-3 space-y-3 max-h-64 overflow-y-auto scrollbar-thin">
            {comments === null && <div className="text-xs text-muted-foreground">Loading…</div>}
            {comments && comments.length === 0 && <div className="text-xs text-muted-foreground">No comments yet. Start the conversation.</div>}
            {comments?.map((c) => (
              <div key={c.id} className="flex gap-2.5">
                <Avatar seed={c.author_avatar_seed} alias={c.author_alias} size={30} />
                <div className="bg-secondary rounded-lg px-3 py-2 flex-1">
                  <div className="text-xs font-semibold">{c.author_alias} <span className="text-muted-foreground font-normal font-mono">· {timeAgo(c.created_at)}</span></div>
                  <div className="text-sm mt-0.5">{c.text}</div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={addComment} className="flex gap-2">
            <Input data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add your voice…" />
            <Button data-testid="comment-submit" type="submit" size="icon" disabled={posting} className="bg-saffron text-primary-foreground hover:bg-saffron/90">
              {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
          </form>
        </div>
      )}
    </article>
  );
}
